
import CopyIcon from '@mui/icons-material/ContentCopy'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import Checkbox from '@mui/material/Checkbox'
import CircularProgress from '@mui/material/CircularProgress'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Tooltip from '@mui/material/Tooltip'
import CardActionsMenu from 'components/Card/CardActionsMenu'
import CardComposeButton from 'components/Card/CardComposeButton'
import ProgressMedia from 'components/Card/ProgressMedia'
import ConfirmDialog from 'components/ConfirmDialog'
import { NOTIFICATION_DURATION_LONG } from 'components/ConnectedSnackbar/ConnectedSnackbar'
import GifPlayer from 'components/GifPlayer'
import { NativeVideoPlayer } from 'components/VideoPlayer'
import { format } from 'date-fns'
import { GIF_TYPE, MyFile, PHOTO_TYPE, VIDEO_TYPE } from 'interfaces'
import * as React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { from, of as observableOf, Subject } from 'rxjs'
import { catchError, mergeMap } from 'rxjs/operators'
import { promptCompose } from 'services/compose'
import { composerImageUrlsSelector, multiPostsArraySelector } from 'services/compose/selectors'
import { addContentToSelection, removeContentFromSelection } from 'services/content'
import { HTTP_STATUS_BAD_REQUEST } from 'services/net'
import { action, message } from 'services/snackbar'
import { openComposerContentDialog } from 'services/ui'
import { deleteFile, getStorageInfo } from 'services/uploads/actions'
import { StoreThunkDispatch } from 'store/state'
import { fromNow } from 'utils/format/date'
import styles from './FileCard.pcss'

const BASE_TEN = 10
const cssVariables = require('styles/variables')
const LOADER_SIZE = 36
const CARD_SIZE_SMALL = parseInt(cssVariables['--square-card-size-small'], BASE_TEN)
const CARD_SIZE_DEFAULT = parseInt(cssVariables['--square-card-size-default'], BASE_TEN)

export interface FileCardProps {
  file: MyFile
  selected?: boolean
  small?: boolean
  className?: string
  hideDetails?: boolean
  actions?: React.ReactNode
  checkboxClassName?: string
  onToggleSelected?: (id: string) => void
  onCreatePost?: (file: MyFile) => void
  onClick?: (file: MyFile) => void
}

export function FileCard(props: FileCardProps) {
  const intl = useIntl()
  const dispatch = useDispatch<StoreThunkDispatch>()
  const multiPosts = useSelector(multiPostsArraySelector)
  const composerCarouselImages = useSelector(composerImageUrlsSelector)
  const [pop, setPop] = React.useState(false)
  const [hovered, setHovered] = React.useState(false)
  const [imageLoading, setImageLoading] = React.useState(true)
  const [imgSrc, setImgSrc] = React.useState(props.file.thumbUrl)
  const delete$ = React.useRef<Subject<{ file: MyFile, force?: boolean }>>(undefined)

  React.useEffect(() => {
    delete$.current = new Subject()
    delete$.current
      .pipe(
        mergeMap(({ file, force }) => {
          return from(dispatch(deleteFile({ file, force })).unwrap())
            .pipe(
              catchError(response => {
                if (response.status === HTTP_STATUS_BAD_REQUEST && response.response.msg === 'File used in posts.') {
                  dispatch(action(
                    'warning',
                    intl.formatMessage({ id: 'find.my-uploads.messages.confirm-delete-used-file' }),
                    intl.formatMessage({ id: 'actions.delete' }),
                    () => {
                      delete$.current?.next({ file, force: true })
                    },
                    NOTIFICATION_DURATION_LONG
                  ))
                  return observableOf({ error: response, handled: true })
                }
                return observableOf({ error: response })
              }),
              mergeMap((response: any) => {
                if (!response.error) {
                  return dispatch(getStorageInfo())
                }
                return observableOf(response)
              })
            )
        })
      )
      .subscribe((result) => {
        if (result.error && !result.handled) {
          const reason = result.error.response?.invalid_files?.[0]?.reason
          if (reason) {
            dispatch(message('Delete failed: ' + reason, 'error'))
          } else {
            const msg = result.response?.msg || result.error.response?.msg || result.error.message || ''
            dispatch(message(intl.formatMessage({ id: 'errors.generic' }) + ` ${msg}`, 'error'))
          }
        }
      })
  }, [dispatch, intl])

  const file = props.file
  const classNamePop = pop ? styles.pop : ''
  const classNameSmall = props.small ? styles.small : ''
  const classNameLoading = props.file.loading ? styles.loading : ''
  const classNameImgLoading = imageLoading ? styles['img-loading'] : ''
  const classType = file.type === PHOTO_TYPE ? styles.img : ''
  const optionalClasses = `${classNameSmall} ${classNamePop} ${classNameLoading} ${classNameImgLoading} ${classType}`
  const time = fromNow(file.createdAt)
  const dateTimeString = format(file.createdAt, 'MMM d, yyyy')

  const onImageError = () => {
    if (file.type === PHOTO_TYPE && imgSrc !== props.file.url) {
      setImgSrc(props.file.url)
    }
  }

  const stopImageLoading = () => {
    setImageLoading(false)
  }

  const createPost = () => {
    if (props.onCreatePost) {
      // EXPL: Open prompt if composer already has carousel or multi-post
      if (composerCarouselImages.length > 1 || multiPosts.length > 0) {
        dispatch(promptCompose({ content: { type: PHOTO_TYPE, imageUrl: file.url } as any, action: 'create' }))
        return
      }

      props.onCreatePost(props.file)
    }
  }

  const onDelete = () => {
    dispatch(removeContentFromSelection([file.id]))
    delete$.current?.next({ file: props.file, force: false })
  }

  const onMouseOver = () => {
    setHovered(true)
    setPop(true)
  }

  const onMouseOut = () => {
    setHovered(false)
  }

  const onTransitionEnd = () => {
    if (!hovered && pop) {
      setPop(false)
    }
  }

  const onCardClick = () => {
    if (props.onClick) {
      props.onClick(props.file)
    }
  }

  const cardHeight = props.small ? CARD_SIZE_SMALL : CARD_SIZE_DEFAULT

  const contentBlockStyles = {
    width: `${file.width / (file.height / cardHeight)}px`,
    height: `${cardHeight}px`
  }

  const openInStudio = () => {
    dispatch(openComposerContentDialog({ tab: 'edit', imageUrl: file.url }))
  }

  const toggleFileSelected = () => {
    if (props.selected) {
      dispatch(removeContentFromSelection([file.id]))
    } else {
      dispatch(addContentToSelection([file]))
    }

    if (props.onToggleSelected) {
      props.onToggleSelected(props.file.id)
    }
  }

  const copyLink = () => {
    navigator.clipboard.writeText(file.url)
    dispatch(message('Link copied to clipboard', 'info'))
  }

  const backgroundStyles = {
    backgroundImage: `url(${imgSrc})`
  }

  return (
    <Paper
      onMouseOver={onMouseOver}
      onMouseOut={onMouseOut}
      onTransitionEnd={onTransitionEnd}
      data-testid="file-card"
      className={`${styles.card} ${props.className || ''} ${optionalClasses}`}
    >
      {imgSrc && <div className={styles['bg-image']} style={backgroundStyles}></div>}
      <Checkbox
        className={`${styles.cb} ${props.checkboxClassName}`}
        checked={props.selected}
        disableRipple
        data-testid="btn-select"
        onChange={toggleFileSelected}
      />
      {imageLoading ? (
        <div className={styles.content} style={contentBlockStyles}>
          <ProgressMedia
            image={imgSrc || ''}
            loader={<CircularProgress size={LOADER_SIZE} className={styles.loader} />}
            className={styles['loader-box']}
            onDone={stopImageLoading}
            onError={stopImageLoading}
          />
        </div>
      ) : (
        <div className={styles.content} style={contentBlockStyles} onClick={onCardClick} data-testid="file-card-content">
          {
            file.type === PHOTO_TYPE && (
              <img src={imgSrc} className={styles.image} onError={onImageError} />
            )
          }
          {
            file.type === VIDEO_TYPE && (
              <NativeVideoPlayer
                videoUrl={file.url}
                thumbnailUrl={file.thumbUrl}
                togglePlayOnHover
                muted
                loop
                className={styles['video-box']}
              />
            )
          }
          {
            file.type === GIF_TYPE && (
              <GifPlayer
                loop
                gifUrl={file.url}
                className={styles['gif-box']}
              />
            )
          }
        </div>
      )}
      <div className={styles.footer} data-name={file.name}>
        {!props.hideDetails && (
          <div className={styles.time}>
            <Tooltip title={dateTimeString}><span data-time={file.createdAt}>{time}</span></Tooltip>
          </div>
        )}
        <span className={`${styles.name} text-ellipsis`}>{file.name}</span>
      </div>

      {props.actions && (
        <div className={styles.actions}>
          {props.actions}
          {/* {sendToAIAction} */}
        </div>
      )}
      {!props.actions && (
        <React.Fragment>
          <CardActionsMenu className={styles['menu-trigger']} keepMounted>
            {(close) => [
              file.type === PHOTO_TYPE && (
                <MenuItem
                  key="studio"
                  onClick={() => {
                    close()
                    openInStudio()
                  }}
                >
                  <EditIcon className={styles['menu-icon']} />
                  <FormattedMessage id="content-card.actions.edit-image" />
                </MenuItem>
              ),
              <MenuItem
                key="copy"
                onClick={() => {
                  close()
                  copyLink()
                }}
              >
                <CopyIcon className={styles['menu-icon']} />
                <FormattedMessage id="content-card.actions.copy-link" />
              </MenuItem>,
              <ConfirmDialog
                key="delete"
                message={intl.formatMessage({ id: 'find.my-uploads.messages.confirm-delete-file' }, { br: <br /> } as any)}
                variant="danger"
                confirmLabel={intl.formatMessage({ id: 'actions.delete' })}
              >
                {
                  (confirm) => (
                    <MenuItem
                      onClick={confirm(onDelete, close)}
                      className={styles['menu-item-delete']}
                      data-testid="btn-delete"
                    >
                      <DeleteIcon className={styles['menu-icon-delete']} />
                      <FormattedMessage id="actions.delete" />
                    </MenuItem>
                  )
                }
              </ConfirmDialog>
            ].filter(Boolean)}
          </CardActionsMenu>

          <CardComposeButton className={styles['compose-btn']} onClick={createPost} />
        </React.Fragment>
      )}
    </Paper>
  )
}

export default FileCard
